import { RegistrationPage,RegistrationStatusPage,RegistrationConfirmationPage } from "./routes/billing/registration-pages";
import { PurchasePage, PurchaseStatusPage, OrganizationBillingPage } from "./routes/billing/pages";
import type { Me, MyContext } from "@gioia365/contracts";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type ParsedLocation,
  type RouterHistory,
} from "@tanstack/react-router";
import { authApi } from "./api/auth";
import { ApiRequestError, type ClientErrorCode } from "./api/http";
import { contextQuery, meQuery, myMembershipsQuery, platformOrganizationsQuery } from "./api/queries";
import { ActivityPage } from "./activity/ActivityPage";
import { validateActivitySearch } from "./activity/activity-search";
import { validateConversationsSearch } from "./conversations/conversation-search";
import { ConversationsPage } from "./conversations/ConversationsPage";
import { PublicShell } from "./design/components/PublicShell";
import { useOptionalAccess } from "./session/access";
import { AppShell } from "./design/components/AppShell";
import { ToastProvider } from "./design/components/Toast";
import { LocaleSync } from "./i18n/locale";
import { CloseImportPage, validateCloseImportSearch } from "./imports/CloseImportPage";
import { GhlReadPage } from "./imports/GhlReadPage";
import { ImportExportPage } from "./imports/ImportExportPage";
import { LeadCardPage } from "./leads/card/LeadCardPage";
import { LeadListPage, validateLeadListSearch } from "./leads/LeadListPage";
import { OpportunitiesPage } from "./opportunities/OpportunitiesPage";
import { validateOpportunitiesSearch } from "./opportunities/opportunity-search";
import { validateReportsSearch } from "./reports/report-search";
import { ReportsPage } from "./reports/ReportsPage";
import { ChooseOrganizationPage } from "./routes/choose-organization";
import { ErrorPage } from "./routes/error-page";
import { HomePage } from "./routes/home";
import { InvitationPage } from "./routes/invitation";
import { LoginPage } from "./routes/login";
import { PlatformOrganizationsPage } from "./routes/platform/organizations";
import { ResetPasswordPage } from "./routes/reset-password";
import { AssignmentPage } from "./routes/settings/assignment";
import { GoalsPage } from "./routes/settings/goals";
import { ConnectionsPage } from "./routes/settings/connections";
import { LeadIntakePage } from "./routes/settings/lead-intake";
import { CalendarPage } from "./routes/settings/calendar";
import { CallSequencePage } from "./routes/settings/call-sequence";
import { LeadFieldsPage } from "./routes/settings/lead-fields";
import { LeadVisibilityPage } from "./routes/settings/lead-visibility";
import { RolesPage, validateRolesSearch } from "./routes/settings/roles-page";
import { settingsSectionsOf } from "./routes/settings/sections";
import { TeamPage } from "./routes/settings/team";
import { WhatsAppSendingPage } from "./routes/settings/whatsapp-sending";
import { WhatsAppTemplatesPage } from "./routes/settings/whatsapp-templates";
import { StyleguidePage } from "./routes/styleguide";
import { accessOf, menuSubjectOf, smartListScopeOf } from "./session/access";
import { clearTabAddress, companyAddressRewrite, companySlugOf, readTabAddress, writeTabAddress } from "./session/company-address";
import { CHANGE_VIEW_PATH, SIGN_OUT_PATH, SignOutError, SWITCH_TAB_PATH, TECHNICAL_PATHS } from "./session/switch-tab";
import {
  clearTabContext,
  parseCommercialView,
  parseTabContext,
  readTabContext,
  tabKey,
  withCommercialView,
  writeTabContext,
  type CommercialView,
  type TabContext,
} from "./session/tab-context";
import { pickSmartList, prefetchSmartList, rememberedSmartList, rememberSmartList, smartListsQuery } from "./smart-lists/smart-list-queries";
import { SmartListPage, SmartListsStartPage, validateSmartListSearch } from "./smart-lists/SmartListPage";
import { validateLeadCardSearch, validateTasksSearch } from "./tasks/task-search";
import { TasksPage } from "./tasks/TasksPage";

// Albero delle rotte e guardie (piano M1b D2, D3, D17):
//
// root (contesto { queryClient, tabStorage }, avvisi, lingua, pagina d'errore)
// ├── /login, /reimposta-password, /invito, /styleguide                 pubbliche
// └── autenticato (sessione da `GET /api/me`; senza → /login?da=<percorso>)
//     ├── /scegli-azienda, /cambia-azienda, /cambia-vista (la Vista Commerciale, M2), /esci
//     └── guscio (AppShell: rail, «Il tuo account», avviso di D13, fascia della Vista Commerciale)
//         ├── azienda (l'azienda della scheda, convalidata da `GET /api/context`; nel browser ogni sua pagina porta davanti
//         │   l'indirizzo breve dell'azienda, `/academy/lead`, che il `rewrite` toglie e rimette: 4 ottobre 2026)
//         │   ├── /                                                     Home
//         │   ├── /task (la scheda, «Di chi», «Cosa» e l'ordine nell'indirizzo)       Task (M5)
//         │   ├── /lead (ricerca, «Di chi» e ordinamento nell'indirizzo), /lead/$leadId   Lead (M2; `da` per «Torna ai Task»)
//         │   ├── /opportunita («Per fase» o «Tabella», «Quali», «Di chi», campagna e ricerca nell'indirizzo)  Opportunità (M6)
//         │   ├── /conversazioni (periodo, filtri e ricerca nell'indirizzo)              Conversazioni (M4)
//         │   ├── /attivita (periodo, «Di chi», «Che cosa» e ricerca nell'indirizzo)      Attività (M7, SEZ-8)
//         │   ├── /report (periodo e «Di chi» nell'indirizzo)                            Report (M7)
//         │   ├── /smart-list (→ l'ultima lista aperta, o la prima), /smart-list/$smartListId   Smart List (M3)
//         │   └── /impostazioni (→ la prima sezione del menu, di solito Team), /impostazioni/team, /impostazioni/ruoli-e-permessi,
//         │       /impostazioni/calendario-fuso-e-lingua, /impostazioni/campi-della-scheda e /impostazioni/chi-vede-i-lead (M2),
//         │       /impostazioni/sequenza-di-chiamate (M5), /impostazioni/invio-dei-whatsapp e /impostazioni/modelli-whatsapp (M6),
//         │       /impostazioni/ingressi-dei-lead e /impostazioni/assegnazione-dei-lead (M6), /impostazioni/obiettivi (M7),
//         │       /impostazioni/importazione-ed-esportazione e la sua pagina …/close, l'importazione guidata da Close
//         │       (l'esecuzione aperta nell'indirizzo, M2); /impostazioni/importazione-close vi rimanda
//         └── piattaforma (soltanto il Super admin di Gioia365; l'azienda della scheda, se c'è, per il guscio)
//             └── /gioia365/aziende
//
// Le rotte non usano `loader`: i dati stanno nella cache di React Query, con chiavi per azienda, e le
// guardie rimettono nel contesto della rotta ciò che hanno convalidato a ogni navigazione. Dopo il primo
// caricamento gli stessi rifiuti arrivano dalle query rilette in background: li gestisce `handleQueryError`.
//
// Nella Vista Commerciale il menu è quello del Commerciale scelto (decisione del 2 ottobre 2026, `menuSubjectOf`): le
// guardie che scelgono dove portare (`/impostazioni`, `/smart-list`) seguono il suo menu, e il guscio non apre una pagina
// che non ne fa parte (`CommercialViewGate`).

export type RouterContext = { queryClient: QueryClient; tabStorage: Storage };

declare module "@tanstack/history" {
  interface HistoryState {
    /** L'azienda di destinazione di `/cambia-azienda` (D2): si convalida sempre prima dell'uso. */
    nextTab?: unknown;
    /** La pagina che `/cambia-azienda` apre nell'azienda nuova al posto della Home (`switchTab`): si convalida sempre. */
    switchReturn?: unknown;
    /** Il segnale di «Esci» (`signOutTab`): senza, `/esci` non fa uscire. */
    signOut?: unknown;
    /** La Vista Commerciale di destinazione di `/cambia-vista`, oppure `null` per uscirne: si convalida sempre. */
    nextView?: unknown;
    /** La pagina a cui `/cambia-vista` torna: si convalida sempre. */
    viewReturn?: unknown;
    /** Il nome del Commerciale la cui vista il server ha appena rifiutato: la shell lo dice (`CommercialViewClosed`). */
    viewClosed?: unknown;
    /** L'indirizzo breve che la guardia dell'azienda ha appena messo davanti all'indirizzo (4 ottobre 2026). */
    companyAddress?: unknown;
    /**
     * «Chiama» da una riga dei Task (piano M5, Task 18; ruling Q7 e Q57): l'identificativo del Task. La scheda del Lead fa partire
     * la chiamata una volta sola e lo toglie: non sta nell'indirizzo, che ricaricato o condiviso richiamerebbe.
     */
    callFromTasks?: unknown;
  }
}

/**
 * Il ritorno dopo l'accesso: la pagina chiesta, con l'indirizzo dell'azienda (`/academy/lead`), così dopo l'accesso si
 * torna nella stessa azienda; la radice e le rotte tecniche non servono come ritorno.
 */
function loginSearch(location: ParsedLocation): { da?: string } {
  const noReturn = location.publicHref === "/" || TECHNICAL_PATHS.has(location.pathname);
  return noReturn ? {} : { da: location.publicHref };
}

function toLogin(location: ParsedLocation) {
  return redirect({ to: "/login", search: loginSearch(location) });
}

/** La scheda dimentica un'azienda che il server non riconosce più, e tutto ciò che ne aveva letto (D17). */
function forgetTab(queryClient: QueryClient, tabStorage: Storage, tab: TabContext) {
  clearTabContext(tabStorage);
  queryClient.removeQueries({ queryKey: ["scheda", tabKey(tab)] });
}

/**
 * La scheda cambia Vista Commerciale, o ne esce (`null`), e riparte da capo (piano M2 D16): le richieste ancora in volo
 * si annullano e tutto ciò che la scheda aveva letto si toglie, come al cambio di azienda ma soltanto per la sua
 * azienda. Così nessuna riga, conteggio o scheda della vista di prima arriva nella nuova.
 */
async function changeViewOf(queryClient: QueryClient, tabStorage: Storage, tab: TabContext, next: CommercialView | null) {
  await queryClient.cancelQueries({ queryKey: ["scheda", tabKey(tab)] });
  queryClient.removeQueries({ queryKey: ["scheda", tabKey(tab)] });
  writeTabContext(tabStorage, withCommercialView(tab, next));
}

/**
 * Il server non accetta più la Vista Commerciale della scheda (`403 commercial_view_denied`: il Commerciale sospeso o non
 * più User, la funzione tolta): la vista si chiude, l'azienda resta. Soltanto se la richiesta rifiutata portava proprio
 * la vista di adesso: la risposta tardiva di una vista già lasciata non chiude quella nuova.
 */
function refusedView(error: unknown, tab: TabContext): CommercialView | null {
  if (!(error instanceof ApiRequestError) || error.code !== "commercial_view_denied") return null;
  const view = tab.commercialView;
  return view && error.commercialView === view.membershipId ? view : null;
}

/**
 * Un `403 commercial_view_denied` in una guardia. Se rifiuta la vista della scheda, la vista si chiude e la stessa pagina
 * si riapre senza, con l'avviso; se rifiuta una vista già lasciata (una lettura partita prima del cambio), non dice nulla
 * della scheda e la pagina si rilegge da capo con la vista di adesso. In tutti e due i casi l'azienda resta: un rifiuto
 * della vista non porta mai a «Scegli l'azienda».
 */
async function viewRefusalRedirect(context: RouterContext, location: ParsedLocation, tab: TabContext, error: ApiRequestError) {
  const refused = refusedView(error, tab);
  await changeViewOf(context.queryClient, context.tabStorage, tab, refused ? null : (tab.commercialView ?? null));
  return redirect({ href: location.href, replace: true, state: { viewClosed: refused ? refused.displayName : location.state.viewClosed } });
}

/**
 * Dove torna `/cambia-vista`: la sezione di prima, senza i filtri della vista lasciata. La scheda di un Lead è un dato
 * della vista di prima, quindi si torna all'elenco; mai a una rotta tecnica né fuori da Gioia365, altrimenti la Home.
 * La stessa regola per la pagina che `/cambia-azienda` apre nell'azienda nuova (`switchTab` con la pagina di ritorno).
 */
function viewReturnPath(value: unknown): string {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return "/";
  if (TECHNICAL_PATHS.has(value)) return "/";
  return value.startsWith("/lead/") ? "/lead" : value;
}

/** Una sessione scaduta durante una guardia porta all'accesso; ogni altro errore sale alla pagina d'errore. */
async function withSession<T>(context: RouterContext, location: ParsedLocation, load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 401) {
      // L'identità ricordata non vale più: dopo il nuovo accesso la guardia la rilegge.
      context.queryClient.removeQueries({ queryKey: meQuery.queryKey });
      throw toLogin(location);
    }
    throw error;
  }
}

function RootLayout() {
  return (
    <ToastProvider>
      <LocaleSync />
      <Outlet />
    </ToastProvider>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout, errorComponent: ErrorPage });

// `accesso` ed `error`: il ritorno da Google di «Accedi con Google» (AZI-11), che il server manda qui; `error` è il motivo di Better Auth.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { da?: string; accesso?: "google"; error?: string } => ({
    ...(typeof search.da === "string" ? { da: search.da } : {}),
    ...(search.accesso === "google" ? { accesso: "google" as const } : {}),
    ...(typeof search.error === "string" ? { error: search.error } : {}),
  }),
  component: LoginPage,
});
// Il percorso è quello del link stampato da `create-platform-admin` (`buildPasswordResetUrl` nel server).
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: "/reimposta-password", component: ResetPasswordPage });
// Il link dell'invito è `/invito#<token>` (`buildInvitationUrl` nel server): il token sta nel frammento.
const invitationRoute = createRoute({ getParentRoute: () => rootRoute, path: "/invito", component: InvitationPage });
const registrationRoute=createRoute({getParentRoute:()=>rootRoute,path:"/registrazione",component:RegistrationPage});
const registrationStatusRoute=createRoute({getParentRoute:()=>rootRoute,path:"/registrazione/stato",component:RegistrationStatusPage});
const registrationConfirmationRoute=createRoute({getParentRoute:()=>rootRoute,path:"/registrazione/conferma",component:RegistrationConfirmationPage});
const purchaseRoute = createRoute({getParentRoute:()=>rootRoute,path:"/acquisto",component:PurchasePage});
const purchaseStatusRoute = createRoute({getParentRoute:()=>rootRoute,path:"/acquisto/stato",component:PurchaseStatusPage});
const styleguideRoute = createRoute({ getParentRoute: () => rootRoute, path: "/styleguide", component: StyleguidePage });

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "autenticato",
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw toLogin(location);
    return { me };
  },
});

const organizationBillingRoute = createRoute({getParentRoute:()=>authenticatedRoute,path:"/abbonamento",component:OrganizationBillingPage});

const chooseOrganizationRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/scegli-azienda",
  // `googleCalendar`: il ritorno da Google dopo «Collega Google Calendar» (piano M6, R7, Task 6), che il server manda qui.
  validateSearch: (search: Record<string, unknown>): { motivo?: "non-disponibile" | "indirizzo"; googleCalendar?: "connected" | "failed" } => ({
    ...(search.motivo === "non-disponibile" || search.motivo === "indirizzo" ? { motivo: search.motivo } : {}),
    ...(search.googleCalendar === "connected" || search.googleCalendar === "failed" ? { googleCalendar: search.googleCalendar } : {}),
  }),
  beforeLoad: async ({ context, location }) => ({
    memberships: await withSession(context, location, () => context.queryClient.ensureQueryData(myMembershipsQuery)),
  }),
  component: ChooseOrganizationPage,
});

// Cambio di azienda (D2): la destinazione arriva nello stato della cronologia (`switchTab`), mai
// nell'indirizzo. Tutto qui avviene soltanto se la navigazione è partita davvero: una bozza che la ferma
// lascia la scheda com'era.
const switchTabRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: SWITCH_TAB_PATH,
  beforeLoad: async ({ context, location, preload }) => {
    // Un'anteprima della rotta (passaggio del mouse su un link) non deve mai cambiare azienda.
    if (preload) return;
    const next = parseTabContext(location.state.nextTab);
    if (!next) throw redirect({ to: "/scegli-azienda" });
    // Le richieste dell'azienda vecchia ancora in volo si annullano; una risposta tardiva finirebbe comunque
    // in una chiave tolta, che nessuna pagina della nuova azienda legge.
    await context.queryClient.cancelQueries({ queryKey: ["scheda"] });
    context.queryClient.removeQueries({ queryKey: ["scheda"] });
    // La Vista Commerciale è di un'azienda: un'azienda nuova parte sempre senza.
    writeTabContext(context.tabStorage, withCommercialView(next, null));
    // Il reindirizzamento ricarica da capo ogni rotta della Home (o della pagina di ritorno), guardie comprese, con la nuova
    // azienda: nessuna rotta tiene dati propri fuori dalla cache (niente `loader`), quindi non resta nulla di vecchio.
    const back = viewReturnPath(location.state.switchReturn);
    throw back === "/" ? redirect({ to: "/" }) : redirect({ href: back });
  },
});

// La Vista Commerciale della scheda (piano M2 Task 22, D16): la destinazione (o `null` per uscirne) e la pagina a cui
// tornare arrivano nello stato della cronologia (`changeCommercialView`), mai nell'indirizzo. Come il cambio di azienda,
// avviene soltanto se la navigazione è partita davvero: una bozza che la ferma lascia la scheda com'era.
const changeViewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: CHANGE_VIEW_PATH,
  beforeLoad: async ({ context, location, preload }) => {
    if (preload) return;
    const back = viewReturnPath(location.state.viewReturn);
    const tab = readTabContext(context.tabStorage);
    if (!tab) throw redirect({ to: "/scegli-azienda" });
    // Senza una destinazione valida (l'indirizzo scritto a mano, uno stato di un'altra forma) la vista non cambia.
    const leaving = location.state.nextView === null;
    const next = leaving ? null : parseCommercialView(location.state.nextView);
    if (leaving || next) await changeViewOf(context.queryClient, context.tabStorage, tab, next);
    // La guardia dell'azienda rilegge il contesto con la vista nuova: un Commerciale che il server non accetta chiude
    // la vista e lo dice (`refusedView`).
    throw redirect({ href: back, replace: true });
  },
});

const signOutRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: SIGN_OUT_PATH,
  beforeLoad: async ({ context, location, preload }) => {
    if (preload) return;
    // Soltanto «Esci» porta il segnale: `/esci` da un link, dal ritorno dopo l'accesso o scritta a mano (anche
    // `/esci/`, `/ESCI`, `/%65sci`, che il router riconosce come `/esci`) porta alla Home e non fa uscire.
    if (location.state.signOut !== true) throw redirect({ to: "/" });
    const result = await authApi.signOut();
    // Senza la conferma del server la sessione potrebbe essere ancora valida: non si finge l'uscita.
    if (!result.ok) throw new SignOutError();
    clearTabContext(context.tabStorage);
    clearTabAddress(context.tabStorage);
    context.queryClient.clear();
    throw redirect({ to: "/login" });
  },
});

function ShellLayout() {
  const access=useOptionalAccess();
  if(access?.context.billingRecovery)return <PublicShell width="wide"><Outlet /></PublicShell>;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

const shellRoute = createRoute({ getParentRoute: () => authenticatedRoute, id: "guscio", component: ShellLayout });

/**
 * L'azienda che ha quell'indirizzo breve per questa persona: la sua appartenenza; per il Super admin di Gioia365, anche
 * un'azienda attiva di cui non fa parte (come «Entra»). `null` se l'indirizzo non è di un'azienda sua.
 */
async function tabForAddress(context: RouterContext & { me: Me }, location: ParsedLocation, slug: string): Promise<TabContext | null> {
  const memberships = await withSession(context, location, () => context.queryClient.ensureQueryData(myMembershipsQuery));
  const membership = memberships.find((candidate) => candidate.organizationSlug === slug);
  if (membership) return { kind: "membership", membershipId: membership.membershipId };
  if (!context.me.isPlatformAdmin) return null;
  const { organizations } = await withSession(context, location, () => context.queryClient.ensureQueryData(platformOrganizationsQuery));
  const organization = organizations.find((candidate) => candidate.slug === slug && candidate.status === "active");
  return organization ? { kind: "platform", organizationId: organization.id } : null;
}

const companyRoute = createRoute({
  getParentRoute: () => shellRoute,
  id: "azienda",
  beforeLoad: async ({ context, location }) => {
    const { queryClient, tabStorage, me } = context;
    let tab: TabContext | null = readTabContext(tabStorage);
    // L'indirizzo dice l'azienda (`/academy/lead`, 4 ottobre 2026): se non è quella della scheda la scheda cambia azienda,
    // come con «Scegli l'azienda» (cache svuotata, Vista Commerciale chiusa), e la pagina chiesta si apre lì. Un indirizzo
    // che non è di un'azienda della persona porta a «Scegli l'azienda» con il motivo, e la scheda resta dov'era.
    const requested = companySlugOf(location.publicHref);
    if (requested !== null && (!tab || readTabAddress(tabStorage, tab) !== requested)) {
      const target = await tabForAddress(context, location, requested);
      if (!target) throw redirect({ to: "/scegli-azienda", search: { motivo: "indirizzo" }, replace: true });
      if (!tab || tabKey(tab) !== tabKey(target)) {
        await queryClient.cancelQueries({ queryKey: ["scheda"] });
        queryClient.removeQueries({ queryKey: ["scheda"] });
        writeTabContext(tabStorage, withCommercialView(target, null));
      }
      tab = readTabContext(tabStorage);
      if (tab) writeTabAddress(tabStorage, tab, requested);
    }
    if (!tab) {
      // Con una sola azienda la scheda la prende da sé; il Super admin di Gioia365 sceglie sempre, perché può
      // entrare anche nelle aziende di cui non fa parte.
      const memberships = await withSession(context, location, () => queryClient.ensureQueryData(myMembershipsQuery));
      const only = memberships.length === 1 && !me.isPlatformAdmin ? memberships[0] : undefined;
      if (!only) throw redirect({ to: "/scegli-azienda" });
      tab = { kind: "membership", membershipId: only.membershipId };
      writeTabContext(tabStorage, tab);
    }
    const current = tab;
    let myContext: MyContext;
    try {
      myContext = await withSession(context, location, () => queryClient.ensureQueryData(contextQuery(current)));
    } catch (error) {
      if(error instanceof ApiRequestError && error.code === "billing_payment_required") throw redirect({to:"/abbonamento"});
      // La Vista Commerciale che il server non accetta più si chiude, l'azienda della scheda resta.
      if (error instanceof ApiRequestError && error.code === "commercial_view_denied") throw await viewRefusalRedirect(context, location, current, error);
      // 400, 403, 404: il server non riconosce più l'azienda della scheda (appartenenza chiusa, azienda
      // sospesa, valore sconosciuto). La scheda la dimentica e la persona ne sceglie un'altra, con il motivo.
      // 503, gli altri 5xx e la rete salgono alla pagina d'errore senza toccare la scheda (D17).
      if (error instanceof ApiRequestError && [400, 403, 404].includes(error.status)) {
        forgetTab(queryClient, tabStorage, current);
        throw redirect({ to: "/scegli-azienda", search: { motivo: "non-disponibile" } });
      }
      throw error;
    }
    // Un indirizzo senza l'azienda (`/lead`, un link di prima, il ritorno alla Home) prende quello dell'azienda della scheda,
    // che il contesto porta. Il percorso del router non cambia (il `rewrite` aggiunge soltanto l'azienda davanti): senza un
    // segno nello stato della cronologia il router la prenderebbe per la stessa pagina e non riscriverebbe l'indirizzo.
    // L'avviso di una Vista Commerciale appena chiusa resta.
    if(myContext.billingRecovery && location.pathname !== "/impostazioni/team" && location.pathname !== "/impostazioni") throw redirect({to:"/abbonamento"});
    const address = myContext.organization.slug;
    writeTabAddress(tabStorage, current, address);
    if (requested === null) {
      throw redirect({ href: location.href, replace: true, state: { companyAddress: address, viewClosed: location.state.viewClosed } });
    }
    return { tab: current, myContext };
  },
});

const homeRoute = createRoute({ getParentRoute: () => companyRoute, path: "/", component: HomePage });

// «Le cose da fare» (piano M5, Task 18): la scheda («Oggi», «Scaduti», «Prossimi giorni», «Completati»), «Di chi», «Cosa» e
// l'ordine nell'indirizzo.
const tasksRoute = createRoute({ getParentRoute: () => companyRoute, path: "/task", validateSearch: validateTasksSearch, component: TasksPage });

// I Lead (piano M2 Task 17): l'elenco con ricerca, «Di chi» e ordinamento nell'indirizzo, e la scheda di un Lead. La scheda
// aperta dai Task porta da dove arriva (`da=task:<vista>:<chi>`), per «Torna ai Task» (piano M5, Task 18).
const leadListRoute = createRoute({ getParentRoute: () => companyRoute, path: "/lead", validateSearch: validateLeadListSearch, component: LeadListPage });
const leadCardRoute = createRoute({ getParentRoute: () => companyRoute, path: "/lead/$leadId", validateSearch: validateLeadCardSearch, component: LeadCardPage });

// «Opportunità» (piano M6, R8, Task 25): «Per fase» o «Tabella», «Quali», «Di chi», campagna e ricerca nell'indirizzo.
const opportunitiesRoute = createRoute({ getParentRoute: () => companyRoute, path: "/opportunita", validateSearch: validateOpportunitiesSearch, component: OpportunitiesPage });

// «Conversazioni» (piano M4, Task 19): le telefonate con periodo, filtri e ricerca nell'indirizzo.
const conversationsRoute = createRoute({ getParentRoute: () => companyRoute, path: "/conversazioni", validateSearch: validateConversationsSearch, component: ConversationsPage });

// «Attività» («Chi ha fatto cosa», SEZ-8; M7 parte 2): il registro delle azioni con periodo, «Di chi», «Che cosa» e ricerca nell'indirizzo.
const activityRoute = createRoute({ getParentRoute: () => companyRoute, path: "/attivita", validateSearch: validateActivitySearch, component: ActivityPage });

// «Report» (piano M7, Task 3): «Commerciali» con il periodo e «Di chi» nell'indirizzo.
const reportsRoute = createRoute({ getParentRoute: () => companyRoute, path: "/report", validateSearch: validateReportsSearch, component: ReportsPage });

// Le Smart List (piano M3 Task 8). La voce della rail porta a `/smart-list`, che apre l'ultima lista aperta nella scheda
// (lo `Storage` della scheda la ricorda), se è ancora nel menu, altrimenti la prima: il menu è già nella cache, letto in
// anticipo dalla rail. Senza liste, o senza il permesso di vederle, la pagina resta e lo spiega; un menu che non si legge
// lo dice la pagina, con «Riprova». La pagina di una lista ha la bozza dei filtri e l'ordinamento nell'indirizzo.
const smartListsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/smart-list",
  beforeLoad: async ({ context, preload }) => {
    if (preload) return;
    // Il menu della scheda: nella Vista Commerciale, quello del Commerciale scelto.
    const menu = menuSubjectOf(context.me, accessOf(context.myContext), context.tab);
    if (!menu.access || smartListScopeOf(menu.access, "view") === "none") return;
    let lists;
    try {
      ({ lists } = await context.queryClient.ensureQueryData(smartListsQuery(context.tab)));
    } catch {
      return;
    }
    const target = pickSmartList(lists, rememberedSmartList(context.tabStorage));
    if (!target) return;
    // Di solito già in cache (la rail la legge in anticipo); altrimenti la lettura parte prima della pagina.
    void prefetchSmartList(context.queryClient, context.tab, target.id);
    throw redirect({ to: "/smart-list/$smartListId", params: { smartListId: target.id }, replace: true });
  },
  component: SmartListsStartPage,
});
const smartListRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/smart-list/$smartListId",
  validateSearch: validateSmartListSearch,
  // L'ultima lista aperta nella scheda, per la voce della rail; una che non c'è più si scarta quando si sceglie.
  beforeLoad: ({ context, params, preload }) => {
    if (!preload) rememberSmartList(context.tabStorage, params.smartListId);
  },
  component: SmartListPage,
});

// Le Impostazioni si aprono sulla prima sezione del menu (D15): «Calendario, fuso e lingua», la prima del gruppo Azienda (IMP-13),
// oppure, nella Vista Commerciale, la prima sezione del Commerciale scelto. Senza sezioni, Team, che nella vista mostra l'avviso.
const settingsRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/impostazioni",
  beforeLoad: ({ context }) => {
    const [first] = settingsSectionsOf(menuSubjectOf(context.me, accessOf(context.myContext), context.tab));
    throw redirect({ to: first?.to ?? "/impostazioni/team", replace: true });
  },
});
const settingsTeamRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/team", component: TeamPage });
// La scheda («Utenti», «Ruoli», «Per tutte le aziende») e la persona del «Ruolo su misura» stanno nell'indirizzo.
const settingsRolesRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/impostazioni/ruoli-e-permessi",
  validateSearch: validateRolesSearch,
  component: RolesPage,
});
const settingsCalendarRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/calendario-fuso-e-lingua", component: CalendarPage });
// Le Impostazioni della scheda del Lead (piano M2 Task 20).
const settingsLeadFieldsRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/campi-della-scheda", component: LeadFieldsPage });
const settingsLeadVisibilityRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/chi-vede-i-lead", component: LeadVisibilityPage });
// I WhatsApp (piano M6, R1, Task 5a): chi usa quale WhatsApp e con quale numero, e i modelli dell'azienda con la riga «scrivi STOP».
const settingsWhatsAppSendingRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/invio-dei-whatsapp", component: WhatsAppSendingPage });
const settingsWhatsAppTemplatesRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/modelli-whatsapp", component: WhatsAppTemplatesPage });
// La regola della sequenza di chiamate dell'azienda (piano M5, Task 13).
const settingsCallSequenceRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/sequenza-di-chiamate", component: CallSequencePage });
// Le porte da cui i Lead entrano da soli: il sito e le landing, GoHighLevel (piano dell'ingresso del 10 ottobre 2026, I2, Task 4).
const settingsLeadIntakeRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/ingressi-dei-lead", component: LeadIntakePage });
// A chi va un Lead nuovo senza owner scelto (piano M6, Task 13).
const settingsAssignmentRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/assegnazione-dei-lead", component: AssignmentPage });
const settingsGoalsRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/obiettivi", component: GoalsPage });
// «Collegamenti» (M7 parte 2): lo stato di ogni collegamento dell'azienda, in sola lettura.
const settingsConnectionsRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/collegamenti", component: ConnectionsPage });
// «Importazione ed esportazione» (1° ottobre 2026) e, dentro, il rapporto dell'importazione guidata da Close (piano M2
// Task 21), con l'esecuzione aperta nell'indirizzo (`?esecuzione=`). Il vecchio indirizzo della pagina di Close vi rimanda
// con la stessa esecuzione, così un link già dato funziona ancora.
const settingsImportExportRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/importazione-ed-esportazione", component: ImportExportPage });
const settingsCloseImportRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/impostazioni/importazione-ed-esportazione/close",
  validateSearch: validateCloseImportSearch,
  component: CloseImportPage,
});
// La chiave di sola lettura di GoHighLevel e la prova di lettura (INGR-29, 24/10/2026), sorella della pagina di Close.
const settingsGhlReadRoute = createRoute({ getParentRoute: () => companyRoute, path: "/impostazioni/importazione-ed-esportazione/gohighlevel", component: GhlReadPage });
const settingsOldCloseImportRoute = createRoute({
  getParentRoute: () => companyRoute,
  path: "/impostazioni/importazione-close",
  validateSearch: validateCloseImportSearch,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/impostazioni/importazione-ed-esportazione/close", search, replace: true });
  },
});

const platformRoute = createRoute({
  getParentRoute: () => shellRoute,
  id: "piattaforma",
  beforeLoad: async ({ context, location }): Promise<{ tab: TabContext | null; myContext: MyContext | null }> => {
    const { queryClient, tabStorage, me } = context;
    if (!me.isPlatformAdmin) throw redirect({ to: "/" });
    // Le pagine della piattaforma non hanno bisogno di un'azienda. Se la scheda ne ha una, il guscio la mostra
    // (voci della rail, «Il tuo account», avviso di D13): la si convalida come nella rotta `azienda`.
    const tab = readTabContext(tabStorage);
    if (!tab) return { tab: null, myContext: null };
    try {
      return { tab, myContext: await withSession(context, location, () => queryClient.ensureQueryData(contextQuery(tab))) };
    } catch (error) {
      // La sessione scaduta (reindirizzamento all'accesso) e gli errori imprevisti salgono come sempre.
      if (!(error instanceof ApiRequestError)) throw error;
      // Come nelle pagine di un'azienda: la vista rifiutata si chiude, l'azienda resta.
      if (error.code === "commercial_view_denied") throw await viewRefusalRedirect(context, location, tab, error);
      if ([400, 403, 404].includes(error.status)) forgetTab(queryClient, tabStorage, tab);
      // Un'azienda che non risponde non ferma la pagina delle aziende, dove la si può anche sistemare: la
      // pagina resta, senza l'azienda nel guscio (che per un 5xx la scheda non dimentica).
      return { tab: null, myContext: null };
    }
  },
});
const platformOrganizationsRoute = createRoute({ getParentRoute: () => platformRoute, path: "/gioia365/aziende", component: PlatformOrganizationsPage });

const routeTree = rootRoute.addChildren([
  registrationRoute,
  registrationStatusRoute,
  registrationConfirmationRoute,
  purchaseRoute,
  purchaseStatusRoute,
  loginRoute,
  resetPasswordRoute,
  invitationRoute,
  styleguideRoute,
  authenticatedRoute.addChildren([
    organizationBillingRoute,
    chooseOrganizationRoute,
    switchTabRoute,
    changeViewRoute,
    signOutRoute,
    shellRoute.addChildren([
      companyRoute.addChildren([
        homeRoute,
        tasksRoute,
        leadListRoute,
        leadCardRoute,
        opportunitiesRoute,
        conversationsRoute,
        activityRoute,
        reportsRoute,
        smartListsRoute,
        smartListRoute,
        settingsRoute,
        settingsTeamRoute,
        settingsRolesRoute,
        settingsLeadFieldsRoute,
        settingsWhatsAppSendingRoute,
        settingsWhatsAppTemplatesRoute,
        settingsCalendarRoute,
        settingsLeadVisibilityRoute,
        settingsCallSequenceRoute,
        settingsLeadIntakeRoute,
        settingsAssignmentRoute,
        settingsGoalsRoute,
        settingsConnectionsRoute,
        settingsImportExportRoute,
        settingsCloseImportRoute,
        settingsGhlReadRoute,
        settingsOldCloseImportRoute,
      ]),
      platformRoute.addChildren([platformOrganizationsRoute]),
    ]),
  ]),
]);

/**
 * Il router di una scheda: cache e `Storage` della scheda nel contesto (D1, D12). Senza `history` usa quella
 * del browser; i test ne passano una in memoria per ogni scheda.
 */
export function createAppRouter(options: { history?: RouterHistory; queryClient: QueryClient; tabStorage: Storage }) {
  return createRouter({
    routeTree,
    history: options.history,
    context: { queryClient: options.queryClient, tabStorage: options.tabStorage },
    // Il nome dell'azienda negli indirizzi (4 ottobre 2026): `company-address.ts`.
    rewrite: companyAddressRewrite(options.tabStorage),
    defaultErrorComponent: ErrorPage,
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

/** I codici con cui ogni rotta di un'azienda rifiuta l'azienda della scheda (`requireMembership` nel server). */
const TAB_REFUSALS: ReadonlySet<ClientErrorCode> = new Set(["membership_required", "membership_forbidden", "platform_forbidden", "organization_unavailable"]);

function refusesTab(error: ApiRequestError, queryKey: QueryKey): boolean {
  if (![400, 403, 404].includes(error.status)) return false;
  // Il rifiuto della Vista Commerciale riguarda la vista, non l'azienda: lo gestisce `handleQueryError` a parte.
  if (error.code === "commercial_view_denied") return false;
  // Sul contesto della scheda ogni 400, 403 e 404 vale come nella guardia; sulle altre query soltanto i codici
  // che parlano dell'azienda: un permesso negato o una riga sparita sono della schermata, non della scheda.
  return queryKey[2] === "context" || TAB_REFUSALS.has(error.code);
}

/**
 * D17 anche dopo il primo caricamento: le guardie leggono il contesto con `ensureQueryData`, che non rilegge i
 * dati già in cache, quindi un rifiuto arriva più tardi da una query riletta in background. Un 401 su
 * qualunque query porta all'accesso con il ritorno (l'azienda resta); un rifiuto dell'azienda della scheda su
 * una query `["scheda", …]` la fa dimenticare e porta a «Scegli l'azienda» con il motivo, come la guardia. Un
 * rifiuto della Vista Commerciale (`commercial_view_denied`) chiude soltanto la vista, come la guardia, e riapre la
 * stessa pagina senza. Da collegare a `createQueryClient({ onQueryError })`.
 */
export function handleQueryError(router: AppRouter, error: unknown, queryKey: QueryKey): void {
  if (!(error instanceof ApiRequestError)) return;
  const tabPart = queryKey[0] === "scheda" && typeof queryKey[1] === "string" ? queryKey[1] : null;
  if(error.code === "billing_payment_required" && tabPart !== null) {void router.navigate({to:"/abbonamento"});return;}
  const expired = error.status === 401;
  const viewRefused = tabPart !== null && error.code === "commercial_view_denied";
  if (!expired && !viewRefused && !(tabPart !== null && refusesTab(error, queryKey))) return;
  // Durante una navigazione le guardie leggono le stesse query e gestiscono da sé il loro errore: qui si decide
  // dopo, a guardie concluse, e soltanto se nessuno l'ha già fatto (sessione già tolta, scheda già cambiata).
  setTimeout(() => {
    const context = router.options.context;
    if (!context) return;
    const { queryClient, tabStorage } = context;
    if (expired) {
      if (!queryClient.getQueryData(meQuery.queryKey)) return;
      queryClient.removeQueries({ queryKey: meQuery.queryKey });
      void router.navigate({ to: "/login", search: loginSearch(router.state.location) });
      return;
    }
    const tab = readTabContext(tabStorage);
    if (!tab || tabKey(tab) !== tabPart) return;
    if (viewRefused) {
      closeRefusedView(router, error, tab);
      return;
    }
    forgetTab(queryClient, tabStorage, tab);
    // Sulle pagine della piattaforma l'azienda non serve: il guscio si aggiorna e la pagina resta.
    if (router.state.matches.some((match) => match.routeId === platformRoute.id)) {
      void router.invalidate();
      return;
    }
    void router.navigate({ to: "/scegli-azienda", search: { motivo: "non-disponibile" } });
  }, 0);
}

/**
 * La Vista Commerciale rifiutata dal server (`refusedView`) si chiude e la stessa pagina si riapre senza, con l'avviso;
 * l'azienda della scheda resta. Una vista già lasciata, o una scheda che non ne ha, non cambia.
 */
function closeRefusedView(router: AppRouter, error: unknown, tab: TabContext): void {
  const context = router.options.context;
  const refused = refusedView(error, tab);
  if (!context || !refused) return;
  void changeViewOf(context.queryClient, context.tabStorage, tab, null).then(() =>
    router.navigate({ href: router.state.location.href, replace: true, state: { viewClosed: refused.displayName } }),
  );
}

/**
 * Una scrittura rifiutata perché la Vista Commerciale della scheda non vale più (il Commerciale appena sospeso): la
 * finestra che l'ha fatta dice il rifiuto, e la vista si chiude come dopo una lettura rifiutata. Da collegare a
 * `createQueryClient({ onMutationError })`.
 */
export function handleMutationError(router: AppRouter, error: unknown): void {
  if (!(error instanceof ApiRequestError) || error.code !== "commercial_view_denied") return;
  setTimeout(() => {
    const tabStorage = router.options.context?.tabStorage;
    const tab = tabStorage ? readTabContext(tabStorage) : null;
    if (tab) closeRefusedView(router, error, tab);
  }, 0);
}

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}
